import { createTourStore, type TourState, useMe, useUpdateMe } from "@quortex/front-common"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { create } from "zustand"
import { useSegmentList } from "@/queries/segments"

const usePrivateStore = create<TourState>((set, get) =>
  createTourStore({
    steps: [
      {
        body: (
          <div className="flex w-80 flex-col gap-2">
            <p>
              When you add a sub segment, we automatically generate a fallback segment to ensure all
              viewers are covered.
            </p>
            <p>
              This fallback segment initially has the same balancing configuration as the original
              but can be fully customized, as all segments are independent.
            </p>
          </div>
        ),
        placement: "bottom",
        title: "Fallback segment"
      }
    ]
  })(set, get)
)

export const useSegmentationFallbackTour = <T,>(selector: (state: TourState) => T) => {
  // TODO: Replace with useMe tours when flags are available
  const { data: me } = useSuspenseQuery(useMe())
  const { data: segments } = useSuspenseQuery(useSegmentList())
  const { mutateAsync: updateMe } = useMutation(useUpdateMe())
  const seenTours = me.seenTours
  const start = usePrivateStore(state => state.start)
  const setInitialized = usePrivateStore(state => state.setInitialized)
  const initialized = usePrivateStore(state => state.initialized)
  const stopped = usePrivateStore(state => state.currentIndex === -1)
  const seen = seenTours.includes("switch_fallback_segment")
  const rootSeen = seenTours.includes("switch_root_segment")
  const hasFallback = segments.some(segment => segment.isFallback)

  useEffect(() => {
    if (rootSeen && !initialized && !seen && hasFallback) {
      start()
      setInitialized(true)
    }
  }, [initialized, seen, rootSeen, start, setInitialized, hasFallback])

  useEffect(() => {
    if (initialized && stopped !== seen && hasFallback) {
      if (stopped) {
        updateMe({
          seenTours: stopped
            ? Array.from(new Set([...me.seenTours, "switch_fallback_segment"]))
            : me.seenTours.filter(tour => tour === "switch_fallback_segment")
        })
      }
    }
  }, [stopped, initialized, seen, hasFallback, me, updateMe])

  return usePrivateStore(selector)
}
